import { LineChartOutlined } from '@ant-design/icons';
import { Alert, Collapse, Descriptions, Table, Tag } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import PlotlyChartRenderer from './components/PlotlyChartRenderer';
import ToolWrapper from './components/ToolWrapper';

/**
 * Renders the GenerateChart tool response.
 * Shows chart generation status and chart ID.
 */
function GenerateChartTool({ item }) {
  const status = item.response?.status || '';
  const chartId = item.response?.chart_id || item.response?.chart_name || '';
  const isPending = item.response === null || item.response === undefined;

  return (
    <ToolWrapper toolName="Chart Generation" isPending={isPending} icon={<LineChartOutlined />}>
      <Descriptions column={1} bordered size="small">
        {!isPending && (
          <>
            <Descriptions.Item label="Status">
              <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
            </Descriptions.Item>
            {chartId && <Descriptions.Item label="Chart ID">{chartId}</Descriptions.Item>}
          </>
        )}
        {!chartId && !isPending && <Alert message="No chart ID returned" type="info" />}
      </Descriptions>
    </ToolWrapper>
  );
}

GenerateChartTool.propTypes = {
  item: PropTypes.shape({
    response: PropTypes.shape({
      status: PropTypes.string,
      chart_id: PropTypes.string,
      chart_name: PropTypes.string,
    }),
  }).isRequired,
};

/**
 * Renders the ChartBuilder tool response.
 * Shows chart builder request and result details.
 */
function ChartBuilderTool({ item }) {
  const request = item.args?.request || '';
  const result = item.response?.result || '';
  const status = item.response?.status || '';
  const isPending = item.response === null || item.response === undefined;

  return (
    <ToolWrapper toolName="Chart Builder" isPending={isPending} icon={<LineChartOutlined />}>
      {!isPending && status && (
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Status">
            <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
          </Descriptions.Item>
        </Descriptions>
      )}

      {request && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Chart Builder Request</h4>
          <div
            style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            {typeof request === 'string' ? request : JSON.stringify(request, null, 2)}
          </div>
        </div>
      )}

      {result && (
        <div>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Chart Builder Result</h4>
          <div
            style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
          </div>
        </div>
      )}

      {!request && !result && !isPending && (
        <Alert message="No chart builder data available" type="info" />
      )}
    </ToolWrapper>
  );
}

ChartBuilderTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      request: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          chart_type: PropTypes.string,
          question_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ]),
    }),
    response: PropTypes.shape({
      result: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          chart_id: PropTypes.string,
          chart_name: PropTypes.string,
        }),
      ]),
      status: PropTypes.string,
    }),
  }).isRequired,
};

/**
 * Renders the DisplayChart tool response.
 * Shows interactive Plotly chart with full dataset from backend.
 * Renders inline without collapse for immediate visibility.
 */
function DisplayChartTool({ item }) {
  const chartId = item.args?.chart_id || item.args?.chart_name || '';
  const status = item.response?.status || '';
  const message = item.response?.message || '';
  const isPending = item.response === null || item.response === undefined;

  // Extract from nested chart object and render annotation
  const chartObject = item.response?.chart || {};
  const renderAnnotation = item.response?.render_annotation || {};

  // Full chart data for rendering (from backend display_chart tool)
  const chartData = item.response?.chart_data || [];

  // Parse spec if it's a JSON string
  let parsedSpec = chartObject.spec;
  if (typeof parsedSpec === 'string') {
    try {
      parsedSpec = JSON.parse(parsedSpec);
    } catch (e) {
      // Failed to parse chart spec
      parsedSpec = {};
    }
  }

  const chartSpec = parsedSpec || {};
  const chartType = chartObject.chart_type || renderAnnotation.chart_type || '';
  const chartTitle = chartObject.title || renderAnnotation.title || chartId;
  const encodings = chartSpec.encodings || renderAnnotation.encodings || {};

  if (isPending) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Loading chart data...</div>
      </div>
    );
  }

  if (!chartId) {
    return <Alert message="Missing chart ID" type="error" style={{ margin: '8px 0' }} />;
  }

  if (status !== 'success') {
    return (
      <Alert
        message={`Chart display failed - ID: ${chartId}`}
        description={`Status: ${status}. ${message}`}
        type="error"
        style={{ margin: '8px 0' }}
      />
    );
  }

  const hasValidData = chartData && Array.isArray(chartData) && chartData.length > 0;

  // Render chart inline without collapse wrapper
  return (
    <div style={{ marginBottom: 16 }}>
      {hasValidData ? (
        <div style={{ marginBottom: 8 }}>
          <PlotlyChartRenderer
            spec={{ ...chartSpec, encodings }}
            data={chartData}
            chartType={chartType}
            title={chartTitle}
          />
        </div>
      ) : (
        <Alert
          message="No chart data available"
          description="Chart specification exists but no data was provided."
          type="warning"
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Show chart metadata in a subtle collapse */}
      <Collapse ghost size="small">
        <Collapse.Panel
          header={<span style={{ fontSize: 12, color: '#8c8c8c' }}>Chart Details</span>}
          key="1"
        >
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Chart ID">{chartId}</Descriptions.Item>
            {chartType && <Descriptions.Item label="Type">{chartType}</Descriptions.Item>}
            {chartTitle && <Descriptions.Item label="Title">{chartTitle}</Descriptions.Item>}
            <Descriptions.Item label="Has Spec">{chartSpec ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Has Data">{hasValidData ? 'Yes' : 'No'}</Descriptions.Item>
            {hasValidData && (
              <Descriptions.Item label="Data Points">{chartData.length}</Descriptions.Item>
            )}
          </Descriptions>

          {chartSpec && (
            <div style={{ marginTop: 16 }}>
              <strong>Chart Specification:</strong>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: 300,
                  fontSize: 11,
                }}
              >
                {JSON.stringify(chartSpec, null, 2)}
              </pre>
            </div>
          )}

          {hasValidData && (
            <div style={{ marginTop: 16 }}>
              <strong>Chart Data Preview:</strong>
              <Table
                dataSource={chartData.slice(0, 5).map((row, idx) => ({ ...row, key: idx }))}
                columns={Object.keys(chartData[0] || {}).map((key) => ({
                  title: key,
                  dataIndex: key,
                  key,
                  ellipsis: true,
                }))}
                pagination={false}
                size="small"
                scroll={{ x: true }}
                style={{ marginTop: 8 }}
              />
              {chartData.length > 5 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                  ... and {chartData.length - 5} more rows
                </div>
              )}
            </div>
          )}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

DisplayChartTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      chart_id: PropTypes.string,
      chart_name: PropTypes.string,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      message: PropTypes.string,
      chart_id: PropTypes.string,
      chart: PropTypes.shape({
        chart_type: PropTypes.string,
        title: PropTypes.string,
        result_key: PropTypes.string,
        spec: PropTypes.shape({
          encoding: PropTypes.shape({}),
        }),
        description: PropTypes.string,
      }),
      chart_data: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ),
      render_annotation: PropTypes.shape({
        type: PropTypes.string,
        chart_type: PropTypes.string,
        title: PropTypes.string,
        encodings: PropTypes.shape({}),
        format_as_percentage: PropTypes.bool,
        result_key: PropTypes.string,
        total_rows: PropTypes.number,
        columns: PropTypes.arrayOf(PropTypes.string),
        llm_preview: PropTypes.arrayOf(PropTypes.shape({})),
        next_steps_hint: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export { GenerateChartTool, ChartBuilderTool, DisplayChartTool };
